/**
 * PD-17613 / PD-17614 — the built-in vocabulary of a campaign's sequence:
 * step types, waits, the state of each person's step rows, and the run states
 * a member moves through. These are fixed in code, not tenant-editable
 * (docs/claude/campaigns/01-decisions.md: "Campaign member states are built
 * in, not editable"); what a tenant configures is the meaning catalogs
 * (Call Disposition, Reply Category) that map onto them.
 */

/** What one step of a sequence does. Any order is allowed. */
export const CAMPAIGN_STEP_TYPES = ['email', 'call', 'linkedin', 'voicemail_drop', 'task'] as const;
export type CampaignStepType = (typeof CAMPAIGN_STEP_TYPES)[number];

export const CAMPAIGN_STEP_TYPE_LABELS: Readonly<Record<CampaignStepType, string>> = {
	email: 'Email',
	call: 'Call',
	linkedin: 'LinkedIn',
	voicemail_drop: 'Voicemail Drop',
	task: 'Task',
};

/** Steps a person on the team does by hand; they come due on the rep's list. */
export const CAMPAIGN_MANUAL_STEP_TYPES: ReadonlyArray<CampaignStepType> = ['call', 'linkedin', 'voicemail_drop', 'task'];

/**
 * The unit of the wait AFTER a step (Instantly's rule: the wait lives on the
 * earlier step). Business days are counted with the tenant's working week
 * and closures.
 */
export const CAMPAIGN_WAIT_UNITS = ['business_days', 'days', 'hours'] as const;
export type CampaignWaitUnit = (typeof CAMPAIGN_WAIT_UNITS)[number];

/**
 * One person's row for one step (campaign_step_event).
 *
 * - `pending`: waiting for its due time.
 * - `due`: a manual step on the rep's list.
 * - `sending`: an email Workd has handed to Instantly (added or released),
 *   waiting for Instantly to report it sent.
 * - `done`: finished (sent, called, logged).
 * - `skipped`: passed over (no phone, no profile, an address that can't be
 *   emailed) or closed because the run ended.
 *
 * A member has at most one OPEN row (pending, due or sending) at a time.
 */
export const CAMPAIGN_STEP_EVENT_STATUSES = ['pending', 'due', 'sending', 'done', 'skipped'] as const;
export type CampaignStepEventStatus = (typeof CAMPAIGN_STEP_EVENT_STATUSES)[number];

export const CAMPAIGN_OPEN_STEP_EVENT_STATUSES: ReadonlyArray<CampaignStepEventStatus> = ['pending', 'due', 'sending'];

export function isOpenStepEventStatus(status: string | null | undefined): boolean {
	return status != null && (CAMPAIGN_OPEN_STEP_EVENT_STATUSES as ReadonlyArray<string>).includes(status);
}

/**
 * Where a v2 member's run is. `active` and `paused` are running; `ended`
 * carries why in `endedReason`. Mission Control's v1 mirror rows keep their
 * own states (replied, completed, ...) in the same column.
 */
export const CAMPAIGN_MEMBER_RUN_STATES = ['active', 'paused', 'ended'] as const;
export type CampaignMemberRunState = (typeof CAMPAIGN_MEMBER_RUN_STATES)[number];

/** Why a run ended. */
export const CAMPAIGN_MEMBER_ENDED_REASONS = [
	'replied',
	'conversation',
	'meeting',
	'not_interested',
	'bounced',
	'unsubscribed',
	'do_not_call',
	'no_phone',
	'finished',
	'removed',
] as const;
export type CampaignMemberEndedReason = (typeof CAMPAIGN_MEMBER_ENDED_REASONS)[number];

export const CAMPAIGN_MEMBER_ENDED_REASON_LABELS: Readonly<Record<CampaignMemberEndedReason, string>> = {
	replied: 'Replied',
	conversation: 'Had a Conversation',
	meeting: 'Meeting Booked',
	not_interested: 'Not Interested',
	bounced: 'Bounced',
	unsubscribed: 'Unsubscribed',
	do_not_call: 'Do Not Call',
	no_phone: 'No Phone',
	finished: 'Finished',
	removed: 'Removed',
};

/**
 * PD-17734 — where a campaign's mailbox health is kept on its provider link's
 * `stats`, refreshed by the catch-up: the warnings read it, and the engine
 * holds people mid-thread on a mailbox that is down.
 */
export const CAMPAIGN_MAILBOX_HEALTH_KEY = 'workdMailboxHealth';

export interface CampaignMailboxHealthModel {
	working: number;
	down: Array<{ email: string; reason: string }>;
	checkedAt: string;
}

/** Why a run is paused. */
export const CAMPAIGN_MEMBER_PAUSED_REASONS = ['out_of_office', 'manual', 'needs_review'] as const;
export type CampaignMemberPausedReason = (typeof CAMPAIGN_MEMBER_PAUSED_REASONS)[number];

/**
 * Where the person stands in the sending provider (Instantly):
 * `none` until their first email is due, `added` while in the campaign,
 * `stopping` once Workd asked to move them out (a background job there),
 * `stopped` when the read-back confirms they are out of the campaign (kept
 * in the workspace so their history stays readable), `removed` once deleted
 * from the workspace to free a people slot.
 */
export const CAMPAIGN_PROVIDER_LEAD_STATES = ['none', 'added', 'stopping', 'stopped', 'removed'] as const;
export type CampaignProviderLeadState = (typeof CAMPAIGN_PROVIDER_LEAD_STATES)[number];

/** What happens to a call step for a person with no phone number. */
export const CAMPAIGN_NO_PHONE_RULES = ['linkedin', 'skip', 'leave_out'] as const;
export type CampaignNoPhoneRule = (typeof CAMPAIGN_NO_PHONE_RULES)[number];

/** What happens to a LinkedIn step for a person with no LinkedIn profile. */
export const CAMPAIGN_NO_LINKEDIN_RULES = ['skip', 'task'] as const;
export type CampaignNoLinkedInRule = (typeof CAMPAIGN_NO_LINKEDIN_RULES)[number];

/** Kinds of copy a campaign holds (campaign_content.kind). */
export const CAMPAIGN_CONTENT_KINDS = ['email', 'call_script', 'voicemail', 'gatekeeper', 'turnaround', 'linkedin'] as const;
export type CampaignContentKind = (typeof CAMPAIGN_CONTENT_KINDS)[number];

/** How a version of copy came to be (campaign_content_version.source). */
export const CAMPAIGN_CONTENT_VERSION_SOURCES = ['drafted', 'regenerated', 'edited', 'restored', 'variations'] as const;
export type CampaignContentVersionSource = (typeof CAMPAIGN_CONTENT_VERSION_SOURCES)[number];

/** Campaign kinds (the brief's "kind"). */
export const CAMPAIGN_KINDS = ['prospecting', 'event', 'trade_show', 'reengagement', 'announcement'] as const;
export type CampaignKind = (typeof CAMPAIGN_KINDS)[number];

/** Out of office with no readable return date pauses this many calendar days. */
export const CAMPAIGN_OUT_OF_OFFICE_DEFAULT_DAYS = 7;

/**
 * The shared release label for the Nth email of any campaign (N >= 2).
 * One set for every campaign, never one per campaign (02-instantly-engine.md);
 * InstantlySettings hides them from the reply mapping by this pattern.
 */
export function campaignReleaseLabelName(emailNumber: number): string {
	return `Workd: send email ${String(emailNumber)}`;
}

/** The Instantly lead list ended runs are moved to, so their history stays readable until rotation. */
export const CAMPAIGN_ENDED_LEAD_LIST_NAME = 'Workd: ended runs';
